import { Immutable } from "immer";
import React from "react";
import {
    FinancingModalTriggerFinance,
    financingCCs,
} from "tsi-common-react/src/apps/checkout/FinancingModalTriggerFinance/index";
import { FinanceCalculator } from "tsi-common-react/src/apps/financing/FinanceCalculator";
import { FinancingModalTrigger } from "tsi-common-react/src/apps/financing/FinancingModalTrigger";
import { PromotionBlock } from "tsi-common-react/src/components/PromotionBlock/index";
import { jt, t } from "ttag";

import { templates } from "@reactivated";

import { FinancingModalTriggerCheckNow } from "../../apps/checkout/FinancingModalTriggerCheckNow";
import { StearnsFinanceFAQsBlock } from "../../blocks/StearnsFinanceFAQsBlock";
import { StearnsFinanceOptionsBlock } from "../../blocks/StearnsFinanceOptionsBlock";

/**
 * Full-page React assembly for `FinancePage` (#33285, Phase 2 FINAL).
 *
 * The legacy `finance_page.html` `{% block layout %}` rendered the
 * `finance_fields` StreamField in THREE scattered `block_type`-filtered
 * `{% for %}` loops interleaved with static chrome and four `include_react`
 * financing widgets. This reproduces the WHOLE layout in one render tree and
 * dispatches each `finance_fields` block in its original section:
 *   - `finance_options` -> table row     (StearnsFinanceOptionsBlock)
 *   - `promotion`       -> promotion row  (PromotionBlock)
 *   - `faqs`            -> FAQs row        (StearnsFinanceFAQsBlock)
 *
 * The four financing widgets that were separate `include_react` calls are
 * rendered as their React components directly (no nested `include_react`),
 * passing the same props the original `include_react` calls did:
 *   - FinancingCheckNow -> FinancingModalTriggerCheckNow (stearns-local; the
 *     common `FinancingCheckNowTemplate` is an empty fragment, so the stearns
 *     override is the real widget). `classString` == legacy `extra_classes`.
 *   - FinancingForm -> FinancingModalTrigger modalType="full-app"
 *     (mirrors `FinancingFormTemplate`): className/title/applicationSource +
 *     children == legacy link_class/link_title/application_source/link_text.
 *   - FinanceCalculator -> FinanceCalculator (mirrors `FinanceCalculatorTemplate`).
 *   - FinancingTriggerFinance -> FinancingModalTriggerFinance ccSource=stearns
 *     (mirrors `FinancingTriggerFinanceTemplate`): cc_source="stearns",
 *     application_source="Financing Page".
 *
 * Page-level data the client side does not otherwise have (default plan terms,
 * `|richtext`-expanded headline, the pre-approval disclosure HTML, the hero
 * image URL) is threaded by the `get_finance_page_body` templatetag.
 */

const APPLICATION_SOURCE = "Financing Page";

type IProps = Immutable<
    Omit<templates.StearnsFinancePageTemplate, "is_preview" | "page_chrome">
>;

type FinanceField = IProps["page"]["finance_fields"]["value"][number];
type FinanceOptionsField = Extract<FinanceField, { type: "finance_options" }>;
type PromotionField = Extract<FinanceField, { type: "promotion" }>;
type FAQsField = Extract<FinanceField, { type: "faqs" }>;

export const FinancePageBody = (props: IProps): React.JSX.Element => {
    const applyNowText = t`Apply Now`;
    const fields = props.page.finance_fields.value;

    // Filter the single StreamField value into the three sections, mirroring the
    // three legacy `{% for block in self.finance_fields %}` / `{% if
    // block.block_type == ... %}` loops. `promotion` keeps the 1-indexed
    // position WITHIN its own filtered run (matching the toolbox-map
    // `promotion_block` GA4 `creative_slot` convention).
    const financeOptions = fields.filter(
        (b): b is FinanceOptionsField => b.type === "finance_options",
    );
    const promotions = fields.filter(
        (b): b is PromotionField => b.type === "promotion",
    );
    const faqs = fields.filter((b): b is FAQsField => b.type === "faqs");

    // Interpolated link for the Wells Fargo `jt` sentence below.
    const wellsFargoLink = (
        <a key="wf" href="https://wellsfargo.com/cardholders">
            wellsfargo.com/cardholders
        </a>
    );

    return (
        <>
            <div
                className="hero finance-hero l-full-width u-margin-bottom--xl"
                style={
                    props.hero_image_url
                        ? {
                              backgroundImage: `url(${props.hero_image_url})`,
                          }
                        : undefined
                }
            >
                <div className="finance-hero__content l-capped-width">
                    <div
                        className="finance-hero__copy"
                        dangerouslySetInnerHTML={{
                            __html: props.headline_html,
                        }}
                    />
                </div>
                <div className="financing-modal-trigger">
                    <FinancingModalTriggerCheckNow classString="button finance-hero__button finance-hero__button--pre-qual al-financing-page__hero--check-now" />
                </div>
            </div>

            <div className="finance-page-row l-capped-width u-margin-bottom--l">
                <div className="finance-page-offer">
                    <div className="finance-page-offer__inner-container">
                        <h1 className="finance-page-offer__terms">
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--1">
                                {props.apr}% APR
                            </span>
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--2">
                                {t`for`}{" "}
                                <span className="finance-page-offer__line-rule" />
                            </span>
                            <span className="finance-page-offer__terms-line finance-page-offer__terms-line--3">
                                {props.term_months} {t`Months`}
                                <sup>
                                    <a href="#terms_and_conditions">
                                        {props.fine_print_superscript}
                                        <span className="ada-screenreader-only">
                                            {t`Additional information about Terms and Conditions`}
                                        </span>
                                    </a>
                                </sup>
                            </span>
                        </h1>
                        <div className="finance-page-offer__buttons">
                            <FinancingModalTrigger
                                modalType="full-app"
                                className="button finance-page-offer__button finance-page-offer__button--apply"
                                title={applyNowText}
                                applicationSource={APPLICATION_SOURCE}
                            >
                                {applyNowText}
                            </FinancingModalTrigger>
                            <a
                                className="finance-page-offer__button finance-page-offer__button--terms"
                                href="#terms_and_conditions"
                            >
                                {t`Financial Terms`}
                                <span className="ada-screenreader-only">
                                    {t`Additional information about Terms and Conditions`}
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="finance-page-benefits">
                    <ul className="finance-page-benefits__list">
                        <li className="finance-page-benefits__list-item">
                            {t`No annual fee`}
                            <sup>
                                <a href="#terms_and_conditions">
                                    1
                                    <span className="ada-screenreader-only">
                                        {t`Additional information about Terms and Conditions`}
                                    </span>
                                </a>
                            </sup>
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`Quick and secure online application`}
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`Apply now, or in checkout`}
                        </li>
                        <li className="finance-page-benefits__list-item">
                            {t`The Stearns & Foster Credit Card cannot be used for purchases at any other merchants`}
                        </li>
                    </ul>
                </div>
            </div>

            <div className="finance-page-row l-capped-width u-margin-bottom--l">
                <div className="finance-table">
                    {financeOptions.map((block) => (
                        <StearnsFinanceOptionsBlock
                            key={block.id}
                            value={block.value}
                        />
                    ))}
                </div>
                <div className="finance-calculator-container">
                    <FinanceCalculator />
                </div>
            </div>

            <div className="finance-page-row finance-page-row--usage-and-help l-capped-width u-margin-bottom--l">
                <div className="finance-help">
                    <h5 className="finance-help__title">
                        {t`Need help with your existing Wells Fargo Account?`}
                    </h5>
                    <div className="finance-help__content">
                        <a
                            href="https://retailservices.wellsfargo.com/customer/index.html?returnFull=1"
                            target="_blank"
                            rel="noreferrer"
                            className="button button--inverse finance-help__button"
                        >
                            {t`Access your Account`}
                        </a>
                        <span>
                            {/* Single `{% blocktrans %}` with an embedded link in
                                the legacy template -> kept as one translatable
                                sentence via `jt` (codebase convention). */}
                            {jt`Contact Wells Fargo Bank, N.A. at 877-805-7744 or sign on at ${wellsFargoLink} to manage your account.`}
                            <a
                                href="https://wellsfargo.com/cardholders"
                                className="finance-help__link al-financing-page__acccess-your-account"
                            >
                                {t`Access your Account`}
                            </a>
                        </span>
                    </div>
                </div>
            </div>

            <div className="finance-page-row l-capped-width u-margin-bottom--l">
                {promotions.map((block, idx) => (
                    <div key={block.id} className="finance-page__promotion">
                        <PromotionBlock
                            value={block.value}
                            blockIndex={idx + 1}
                        />
                    </div>
                ))}
            </div>

            <div className="finance-page__pre-approval-ad--mobile finance-page__pre-approval-ad--mobile-alternate pre-approval-ad pre-approval-ad--finance-page u-flex-container">
                <div className="pre-approval-ad__or-and-see">
                    <div className="pre-approval-ad__see">
                        <div className="pre-approval-ad__see--bold">
                            {t`Check if you're pre-qualified`}
                        </div>
                        <div className="pre-approval-ad__see--small">
                            {t`without affecting your credit score.`}
                        </div>
                    </div>
                </div>
                <FinancingModalTriggerCheckNow classString="button pre-approval-ad__button pre-approval-ad__button--mobile pre-approval-ad__check" />
                <div className="pre-approval-ad__apply-alternate">
                    {t`or`}{" "}
                    <FinancingModalTrigger
                        modalType="full-app"
                        className="pre-approval-ad__apply-alternate-link"
                        title={applyNowText}
                        applicationSource={APPLICATION_SOURCE}
                    >
                        {applyNowText}
                    </FinancingModalTrigger>
                </div>
            </div>

            <div className="finance-page-row finance-page-row--faqs l-capped-width u-margin-bottom--xl">
                <h3 className="finance-page__header finance-faqs__header">
                    {t`Frequently Asked Questions`}
                </h3>
                {faqs.map((block) => (
                    <div key={block.id} className="finance-faqs__faqs">
                        <StearnsFinanceFAQsBlock value={block.value} />
                    </div>
                ))}
            </div>

            <div
                className="finance-page-row finance-page__terms l-capped-width u-margin-bottom--l"
                id="terms_and_conditions"
                dangerouslySetInnerHTML={{ __html: props.terms_html }}
            />

            <div className="finance-page__pre-approval-ads l-capped-width u-margin-bottom--l">
                <FinancingModalTriggerFinance
                    ccSource={financingCCs.STEARNS}
                    applicationSource={APPLICATION_SOURCE}
                />
            </div>
        </>
    );
};
